import { QueryClient, QueryClientProvider, useQuery } from '@tanstack/react-query';
import { createRootRoute, createRoute, createRouter, Navigate, Outlet, RouterProvider, useNavigate, useRouterState } from '@tanstack/react-router';
import { StrictMode, type ReactNode } from 'react';
import { createRoot } from 'react-dom/client';
import { api, ApiError, basePath, type Me } from './api';
import { I18nProvider } from './i18n';
import { AdminLoginPage, AdminPage } from './pages/admin';
import { ForgotPage, LoginPage, NewPasswordPage, RegisterPage, ResetPage } from './pages/auth';
import { CompanyPage, ProjectsPage, ServicesPage, TemplatesPage } from './pages/catalog';
import { IncomingFormPage } from './pages/incoming';
import { InvoiceFormPage } from './pages/invoices';
import { InvoicesPage } from './pages/sales';
import { AgreementsPage } from './pages/agreements';
import { DashboardPage } from './pages/overview';
import { CalendarPage } from './pages/calendar';
import { SupportPage } from './pages/support';
import { BankPage } from './pages/bank';
import { ClosePage } from './pages/close';
import { ShopPage } from './pages/shop';
import { RecurringPage } from './pages/recurring';
import { InvitePage } from './pages/team';
import { TaxesPage } from './pages/taxes';
import { CashPage } from './pages/cash';
import { IvPage } from './pages/iv';
import { FirmPage } from './pages/firm';
import { PayrollPage } from './pages/payroll';
import { AssistantPage } from './pages/assistant';
import { FilesPage } from './pages/files';
import { PhoneLinkPage } from './pages/phone-link';
import { ConfirmSenderPage } from './pages/confirm-sender';
import { ViewConsentPage } from './pages/view-consent';
import { ReconciliationPage, type ReconciliationTab } from './pages/reconciliation';
import { SoonPage } from './pages/soon';
import { SuppliersPage } from './pages/suppliers';
import { AccountantQueuePage } from './pages/accountant-queue';
import { PayPage } from './pages/pay';
import { PaymentsPage } from './pages/payments';
import { QuotesPage } from './pages/quotes';
import { ExportPage } from './pages/export';
import { AccountsPage } from './pages/accounts';
import { JournalPage } from './pages/journal';
import { BooksPage } from './pages/books';
import { AssetsPage } from './pages/assets';
import { DividendsPage } from './pages/dividends';
import { DeclarationsPage } from './pages/declarations';
import { ActConfirmPage } from './pages/act-confirm';
import { type ViewerType, ViewerPage } from './pages/viewer';
import { type ListSearch, validateListSearch } from './pages/filters';
import { NotesPage } from './pages/notes';
import { AccountPage, CustomersPage } from './pages/panel';
import { TasksPage } from './pages/tasks';
import './styles/panel.css';
import './styles/mobile.css';
import './styles/fsp.css';
import './styles/platform.css';

const queryClient = new QueryClient({
  defaultOptions: { queries: { retry: (count, error) => !(error instanceof ApiError && error.status < 500) && count < 2, staleTime: 15_000 } },
});

/** Loads the signed-in user; sends visitors to sign-in and temporary-password holders to /password. */
function WithMe({ children, allowPasswordChange = false }: { children: (me: Me) => ReactNode; allowPasswordChange?: boolean }) {
  const me = useQuery({ queryKey: ['me'], queryFn: () => api.get<Me>('api/v1/me') });
  if (me.isPending) return null;
  if (me.isError) return me.error instanceof ApiError && me.error.status === 401 ? <Navigate to="/login" /> : <p className="alert alert--error">{me.error.message}</p>;
  if (me.data.user.mustChangePassword && !allowPasswordChange) return <Navigate to="/password" />;
  return <>{children(me.data)}</>;
}

function Root() {
  const admin = useRouterState({ select: (s) => s.location.pathname.startsWith('/admin') });
  return <I18nProvider key={admin ? 'admin' : 'panel'} context={admin ? 'admin' : 'panel'}><Outlet /></I18nProvider>;
}

const rootRoute = createRootRoute({ component: Root });
const route = <P extends string>(path: P, component: () => ReactNode) => createRoute({ getParentRoute: () => rootRoute, path, component });

const loginRoute = createRoute({
  getParentRoute: () => rootRoute, path: '/login',
  validateSearch: (s: Record<string, unknown>): { registered?: number; reset?: number; closed?: number; joined?: number; oauth?: string } => ({
    ...(s.registered ? { registered: 1 } : {}), ...(s.reset ? { reset: 1 } : {}), ...(s.closed ? { closed: 1 } : {}), ...(s.joined ? { joined: 1 } : {}),
    // Back from Apple / Google with a reason it did not work.
    ...(typeof s.oauth === 'string' && /^[a-z_]{1,40}$/.test(s.oauth) ? { oauth: s.oauth } : {}),
  }),
  component: function Login() {
    const { registered, reset, closed, joined, oauth } = loginRoute.useSearch();
    const notice = registered ? 'Registracija gauta. Kai administratorius patvirtins paskyrą, galėsite prisijungti.' : reset ? 'Slaptažodis pakeistas. Prisijunkite su nauju slaptažodžiu.' : closed ? 'Paskyra uždaryta. Ačiū, kad naudojotės FakturaSmart Pro.'
      : joined ? 'Paskyra sukurta ir prijungta prie įmonės. Prisijunkite.' : undefined;
    return <LoginPage {...(notice ? { notice } : {})} {...(oauth ? { oauth } : {})} />;
  },
});

const resetRoute = createRoute({
  getParentRoute: () => rootRoute, path: '/reset',
  validateSearch: (s: Record<string, unknown>): { token?: string } => (typeof s.token === 'string' ? { token: s.token } : {}),
  component: function Reset() { return <ResetPage token={resetRoute.useSearch().token ?? ''} />; },
});

const adminRoute = createRoute({
  getParentRoute: () => rootRoute, path: '/admin',
  validateSearch: (s: Record<string, unknown>): { view?: 'pending' | 'active' | 'rejected' | 'suspended' | 'closed' | 'all' | 'email' | 'connected' | 'support' | 'messages' | 'shop' | 'contracts' | 'ai' | 'coupons' } =>
    typeof s.view === 'string' && ['pending', 'active', 'rejected', 'suspended', 'closed', 'all', 'email', 'connected', 'support', 'messages', 'shop', 'contracts', 'ai', 'coupons'].includes(s.view) ? { view: s.view as 'pending' } : {},
  component: function Admin() { return <AdminPage view={adminRoute.useSearch().view ?? 'pending'} />; },
});

const panel = <P extends string>(path: P, page: (me: Me) => ReactNode) => route(path, () => <WithMe>{page}</WithMe>);
const calendarRoute = createRoute({
  getParentRoute: () => rootRoute, path: '/calendar',
  validateSearch: (s: Record<string, unknown>): { calendar?: string } => (typeof s.calendar === 'string' ? { calendar: s.calendar } : {}),
  component: function Calendar() {
    const { calendar } = calendarRoute.useSearch();
    return <WithMe>{(me) => <CalendarPage me={me} {...(calendar ? { notice: calendar } : {})} />}</WithMe>;
  },
});

const newInvoiceRoute = createRoute({
  getParentRoute: () => rootRoute, path: '/outgoing/new',
  validateSearch: (s: Record<string, unknown>): { credit?: number; copy?: number; scan?: number; quote?: number } => ({
    ...(Number(s.credit) > 0 ? { credit: Number(s.credit) } : {}), ...(Number(s.copy) > 0 ? { copy: Number(s.copy) } : {}), ...(Number(s.scan) > 0 ? { scan: Number(s.scan) } : {}),
    ...(Number(s.quote) > 0 ? { quote: Number(s.quote) } : {}),
  }),
  component: function NewInvoice() {
    const { credit, copy, scan, quote } = newInvoiceRoute.useSearch();
    return <WithMe>{(me) => <InvoiceFormPage me={me} {...(credit ? { creditOf: credit } : {})} {...(copy ? { copyOf: copy } : {})} {...(scan ? { scanId: scan } : {})} {...(quote ? { quoteOf: quote } : {})} />}</WithMe>;
  },
});
const newIncomingRoute = createRoute({
  getParentRoute: () => rootRoute, path: '/incoming/new',
  validateSearch: (s: Record<string, unknown>): { scan?: number } => (Number(s.scan) > 0 ? { scan: Number(s.scan) } : {}),
  component: function NewIncoming() {
    const { scan } = newIncomingRoute.useSearch();
    return <WithMe>{(me) => <IncomingFormPage me={me} {...(scan ? { scanId: scan } : {})} />}</WithMe>;
  },
});
const newRecurringRoute = createRoute({
  getParentRoute: () => rootRoute, path: '/recurring/new',
  validateSearch: (s: Record<string, unknown>): { from?: number } => (Number(s.from) > 0 ? { from: Number(s.from) } : {}),
  component: function NewRecurring() {
    const { from } = newRecurringRoute.useSearch();
    return <WithMe>{(me) => <InvoiceFormPage me={me} {...(from ? { repeatOf: from } : {})} />}</WithMe>;
  },
});
const recurringRoute = createRoute({
  getParentRoute: () => rootRoute, path: '/recurring/$id',
  component: function EditRecurring() {
    const { id } = recurringRoute.useParams();
    return <WithMe>{(me) => <InvoiceFormPage me={me} scheduleId={Number(id)} />}</WithMe>;
  },
});
const quoteRoute = createRoute({
  getParentRoute: () => rootRoute, path: '/quotes/$id',
  component: function EditQuote() {
    const { id } = quoteRoute.useParams();
    return <WithMe>{(me) => <InvoiceFormPage me={me} quoteId={Number(id)} />}</WithMe>;
  },
});
const incomingRoute = createRoute({
  getParentRoute: () => rootRoute, path: '/incoming/$id',
  component: function EditIncoming() {
    const { id } = incomingRoute.useParams();
    return <WithMe>{(me) => <IncomingFormPage me={me} invoiceId={Number(id)} />}</WithMe>;
  },
});
const invoiceRoute = createRoute({
  getParentRoute: () => rootRoute, path: '/outgoing/$id',
  component: function EditInvoice() {
    const { id } = invoiceRoute.useParams();
    return <WithMe>{(me) => <InvoiceFormPage me={me} invoiceId={Number(id)} />}</WithMe>;
  },
});

/** Sąskaitos: the invoice lists keep their tab, list and filters in the address (see pages/filters.tsx). */
const invoicesRoute = createRoute({
  getParentRoute: () => rootRoute, path: '/invoices', validateSearch: validateListSearch,
  component: function Invoices() {
    const search = invoicesRoute.useSearch();
    const navigate = useNavigate();
    return <WithMe>{(me) => <InvoicesPage me={me} search={search} onSearch={(s: ListSearch) => void navigate({ to: '/invoices', search: s, replace: true })} />}</WithMe>;
  },
});
// The former list addresses (bookmarks, e-mails, the phone app) open the same lists there.
const outgoingRoute = createRoute({
  getParentRoute: () => rootRoute, path: '/outgoing', validateSearch: validateListSearch,
  component: function Outgoing() { return <Navigate to="/invoices" search={{ ...(outgoingRoute.useSearch() as ListSearch), tab: 'out' }} replace />; },
});
const incomingListRoute = createRoute({
  getParentRoute: () => rootRoute, path: '/incoming', validateSearch: validateListSearch,
  component: function Incoming() { return <Navigate to="/invoices" search={{ ...(incomingListRoute.useSearch() as ListSearch), tab: 'in' }} replace />; },
});

const supportRoute = createRoute({
  getParentRoute: () => rootRoute, path: '/support',
  validateSearch: (s: Record<string, unknown>): { ticket?: number } => (Number(s.ticket) > 0 ? { ticket: Number(s.ticket) } : {}),
  component: function Support() {
    const { ticket } = supportRoute.useSearch();
    const navigate = useNavigate();
    return <WithMe>{(me) => <SupportPage me={me} {...(ticket ? { ticketId: ticket } : {})} onOpen={(id) => void navigate({ to: '/support', search: id ? { ticket: id } : {} })} />}</WithMe>;
  },
});

const viewRoute = createRoute({
  getParentRoute: () => rootRoute, path: '/view',
  validateSearch: (s: Record<string, unknown>): { type: ViewerType; id: number; title?: string } => ({
    type: s.type === 'file' || s.type === 'agreement' || s.type === 'inbox' || s.type === 'drive' || s.type === 'quote' || s.type === 'cash' ? s.type : 'invoice', id: Number(s.id) > 0 ? Number(s.id) : 0,
    ...(typeof s.title === 'string' ? { title: s.title.slice(0, 120) } : {}),
  }),
  component: function View() {
    const { type, id, title } = viewRoute.useSearch();
    return <WithMe>{(me) => <ViewerPage me={me} type={type} id={id} {...(title ? { title } : {})} />}</WithMe>;
  },
});

const filesRoute = createRoute({
  getParentRoute: () => rootRoute, path: '/files',
  validateSearch: (s: Record<string, unknown>): { folder?: number } => (Number(s.folder) > 0 ? { folder: Number(s.folder) } : {}),
  component: function Files() {
    const { folder } = filesRoute.useSearch();
    const navigate = useNavigate();
    return <WithMe>{(me) => <FilesPage me={me} folder={folder ?? null} onOpen={(id) => void navigate({ to: '/files', search: id ? { folder: id } : {} })} />}</WithMe>;
  },
});

const confirmSenderRoute = createRoute({
  getParentRoute: () => rootRoute, path: '/confirm-sender',
  validateSearch: (s: Record<string, unknown>): { token?: string } => (typeof s.token === 'string' ? { token: s.token } : {}),
  component: function ConfirmSender() { return <ConfirmSenderPage token={confirmSenderRoute.useSearch().token ?? ''} />; },
});

const inviteRoute = createRoute({
  getParentRoute: () => rootRoute, path: '/invite',
  validateSearch: (s: Record<string, unknown>): { token?: string; oauth?: string } => ({
    ...(typeof s.token === 'string' ? { token: s.token } : {}), ...(typeof s.oauth === 'string' && /^[a-z_]{1,40}$/.test(s.oauth) ? { oauth: s.oauth } : {}),
  }),
  component: function Invite() { const { token, oauth } = inviteRoute.useSearch(); return <InvitePage token={token ?? ''} {...(oauth ? { oauth } : {})} />; },
});
const viewConsentRoute = createRoute({
  getParentRoute: () => rootRoute, path: '/view-consent',
  validateSearch: (s: Record<string, unknown>): { token?: string } => (typeof s.token === 'string' ? { token: s.token } : {}),
  component: function ViewConsent() { return <ViewConsentPage token={viewConsentRoute.useSearch().token ?? ''} />; },
});
const RECONCILIATION_TABS = ['partners', 'transfers', 'ledger', 'acts'];
const reconciliationRoute = createRoute({
  getParentRoute: () => rootRoute, path: '/reconciliation',
  validateSearch: (s: Record<string, unknown>): { tab?: ReconciliationTab } => (typeof s.tab === 'string' && RECONCILIATION_TABS.includes(s.tab) ? { tab: s.tab as ReconciliationTab } : {}),
  component: function Reconciliation() {
    const { tab } = reconciliationRoute.useSearch();
    const navigate = useNavigate();
    return <WithMe>{(me) => <ReconciliationPage me={me} tab={tab ?? 'partners'} onTab={(next) => void navigate({ to: '/reconciliation', search: { tab: next } })} />}</WithMe>;
  },
});
/** A menu tab that is coming: what it will do and "tell me when it is ready". */
const soonRoute = createRoute({
  getParentRoute: () => rootRoute, path: '/soon',
  validateSearch: (s: Record<string, unknown>): { f?: string } => (typeof s.f === 'string' ? { f: s.f.slice(0, 40) } : {}),
  component: function Soon() {
    const { f } = soonRoute.useSearch();
    return <WithMe>{(me) => <SoonPage me={me} id={f ?? ''} />}</WithMe>;
  },
});
const payRoute = createRoute({
  getParentRoute: () => rootRoute, path: '/pay',
  validateSearch: (s: Record<string, unknown>): { t?: string; paid?: number } => ({ ...(typeof s.t === 'string' ? { t: s.t.slice(0, 64) } : {}), ...(s.paid ? { paid: 1 } : {}) }),
  component: function Pay() { const { t, paid } = payRoute.useSearch(); return <PayPage token={t ?? ''} returned={Boolean(paid)} />; },
});
const paymentsRoute = createRoute({
  getParentRoute: () => rootRoute, path: '/payments',
  validateSearch: (s: Record<string, unknown>): { stripe?: string; project?: number } => ({
    ...(s.stripe === 'done' || s.stripe === 'retry' || s.stripe === 'cancelled' ? { stripe: s.stripe } : {}),
    ...(Number.isInteger(Number(s.project)) && Number(s.project) > 0 ? { project: Number(s.project) } : {}),
  }),
  component: function Payments() {
    const { stripe, project } = paymentsRoute.useSearch();
    return <WithMe>{(me) => <PaymentsPage me={me} {...(stripe ? { stripeReturn: stripe } : {})} {...(project ? { stripeProject: project } : {})} />}</WithMe>;
  },
});
const actConfirmRoute = createRoute({
  getParentRoute: () => rootRoute, path: '/act-confirm',
  validateSearch: (s: Record<string, unknown>): { token?: string } => (typeof s.token === 'string' ? { token: s.token } : {}),
  component: function ActConfirm() { return <ActConfirmPage token={actConfirmRoute.useSearch().token ?? ''} />; },
});

const routeTree = rootRoute.addChildren([
  loginRoute,
  route('/register', () => <RegisterPage />),
  route('/forgot', () => <ForgotPage />),
  resetRoute,
  route('/password', () => <WithMe allowPasswordChange>{(me) => (me.user.mustChangePassword ? <NewPasswordPage me={me} /> : <Navigate to="/" />)}</WithMe>),
  panel('/', (me) => <DashboardPage me={me} />),
  route('/receivables', () => <Navigate to="/reconciliation" replace />),
  panel('/bank', (me) => <BankPage me={me} />),
  reconciliationRoute,
  soonRoute,
  panel('/export', (me) => <ExportPage me={me} />),
  panel('/accounts', (me) => <AccountsPage me={me} />),
  panel('/journal', (me) => <JournalPage me={me} />),
  panel('/books', (me) => <BooksPage me={me} />),
  panel('/assets', (me) => <AssetsPage me={me} />),
  panel('/dividends', (me) => <DividendsPage me={me} />),
  panel('/declarations', (me) => <DeclarationsPage me={me} />),
  panel('/fixed-assets', (me) => <AssetsPage me={me} />),
  panel('/suppliers', (me) => <SuppliersPage me={me} />),
  panel('/accountant', (me) => <AccountantQueuePage me={me} />),
  panel('/close', (me) => <ClosePage me={me} />),
  panel('/shop', (me) => <ShopPage me={me} />),
  panel('/recurring', (me) => <RecurringPage me={me} />),
  panel('/taxes', (me) => <TaxesPage me={me} />),
  panel('/cash', (me) => <CashPage me={me} />),
  panel('/iv', (me) => <IvPage me={me} />),
  panel('/firm', (me) => <FirmPage me={me} />),
  panel('/payroll', (me) => <PayrollPage me={me} />),
  panel('/assistant', (me) => <AssistantPage me={me} />),
  newRecurringRoute,
  panel('/quotes', (me) => <QuotesPage me={me} />),
  route('/quotes/new', () => <WithMe>{(me) => <InvoiceFormPage me={me} newQuote />}</WithMe>),
  quoteRoute,
  recurringRoute,
  panel('/customers', (me) => <CustomersPage me={me} />),
  panel('/account', (me) => <AccountPage me={me} />),
  panel('/company', (me) => <CompanyPage me={me} />),
  panel('/projects', (me) => <ProjectsPage me={me} />),
  panel('/services', (me) => <ServicesPage me={me} />),
  panel('/templates', (me) => <TemplatesPage me={me} />),
  invoicesRoute,
  outgoingRoute,
  supportRoute,
  viewRoute,
  filesRoute,
  confirmSenderRoute,
  viewConsentRoute,
  actConfirmRoute,
  payRoute,
  paymentsRoute,
  inviteRoute,
  route('/m/$', () => <PhoneLinkPage />),
  newInvoiceRoute,
  invoiceRoute,
  incomingListRoute,
  panel('/agreements', (me) => <AgreementsPage me={me} />),
  newIncomingRoute,
  incomingRoute,
  panel('/tasks', (me) => <TasksPage me={me} />),
  panel('/notes', (me) => <NotesPage me={me} />),
  calendarRoute,
  route('/admin/login', () => <AdminLoginPage />),
  adminRoute,
]);

const router = createRouter({ routeTree, defaultPreload: 'intent', basepath: basePath || '/' });
declare module '@tanstack/react-router' {
  interface Register { router: typeof router }
}

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <RouterProvider router={router} />
    </QueryClientProvider>
  </StrictMode>,
);
